'use client'

import { Slot } from '@radix-ui/react-slot'
import { useControllableState } from '@radix-ui/react-use-controllable-state'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import {
  addBusinessDays,
  addDays,
  format,
  isSameDay,
  isToday,
  isWeekend,
} from 'date-fns'
import {
  type ButtonHTMLAttributes,
  type ComponentProps,
  createContext,
  type HTMLAttributes,
  type MouseEventHandler,
  type ReactNode,
  useContext,
} from 'react'
import { cn } from '#src/cn.ts'
import { Button } from '#src/components/Button/Button.tsx'

interface MiniCalendarContextType {
  selectedDate: Date | null | undefined
  onDateSelect: (date: Date) => void
  startDate: Date
  onNavigate: (direction: 'prev' | 'next') => void
  days: number
  weekdaysOnly: boolean
}

const MiniCalendarContext = createContext<MiniCalendarContextType | null>(null)

const useMiniCalendar = (): MiniCalendarContextType => {
  const context = useContext(MiniCalendarContext)
  if (!context) {
    throw new Error('MiniCalendar components must be used within MiniCalendar')
  }
  return context
}

const getDays = (
  startDate: Date,
  count: number,
  weekdaysOnly: boolean
): Date[] => {
  const days: Date[] = []
  let currentDate = startDate
  while (days.length < count) {
    if (!weekdaysOnly || !isWeekend(currentDate)) {
      days.push(currentDate)
    }
    currentDate = addDays(currentDate, 1)
  }
  return days
}

interface FormattedDate {
  month: string
  day: string
  weekday: string
}

const formatDate = (date: Date): FormattedDate => {
  const month = format(date, 'MMM')
  const day = format(date, 'd')
  const weekday = format(date, 'EEE')
  return { month, day, weekday }
}

export interface MiniCalendarProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'defaultValue'
> {
  value?: Date
  defaultValue?: Date
  onValueChange?: (date: Date | undefined) => void
  startDate?: Date
  defaultStartDate?: Date
  onStartDateChange?: (date: Date | undefined) => void
  days?: number
  /** Only show weekdays (Monday-Friday), skipping weekends */
  weekdaysOnly?: boolean
}

export const MiniCalendar = ({
  value,
  defaultValue,
  onValueChange,
  startDate,
  defaultStartDate = new Date(),
  onStartDateChange,
  days = 5,
  weekdaysOnly = false,
  className,
  children,
  ...props
}: MiniCalendarProps): ReactNode => {
  const [selectedDate, setSelectedDate] = useControllableState<
    Date | undefined
  >({
    prop: value,
    defaultProp: defaultValue,
    onChange: onValueChange,
  })

  const [currentStartDate, setCurrentStartDate] = useControllableState({
    prop: startDate,
    defaultProp: defaultStartDate,
    onChange: onStartDateChange,
  })

  const handleDateSelect = (date: Date): void => {
    setSelectedDate(date)
  }

  const handleNavigate = (direction: 'prev' | 'next'): void => {
    const current = currentStartDate || new Date()
    const offset = direction === 'next' ? days : -days
    const newStartDate = weekdaysOnly
      ? addBusinessDays(current, offset)
      : addDays(current, offset)
    setCurrentStartDate(newStartDate)
  }

  const contextValue: MiniCalendarContextType = {
    selectedDate: selectedDate || null,
    onDateSelect: handleDateSelect,
    startDate: currentStartDate || new Date(),
    onNavigate: handleNavigate,
    days,
    weekdaysOnly,
  }

  return (
    <MiniCalendarContext.Provider value={contextValue}>
      <div className={cn('flex items-center gap-2', className)} {...props}>
        {children}
      </div>
    </MiniCalendarContext.Provider>
  )
}

export interface MiniCalendarNavigationProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'color'
> {
  direction: 'prev' | 'next'
  asChild?: boolean
}

export const MiniCalendarNavigation = ({
  direction,
  asChild = false,
  children,
  onClick,
  ...props
}: MiniCalendarNavigationProps): ReactNode => {
  const { onNavigate } = useMiniCalendar()
  const Icon = direction === 'prev' ? IconChevronLeft : IconChevronRight

  const handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    onNavigate(direction)
    onClick?.(event)
  }

  if (asChild) {
    return (
      <Slot onClick={handleClick} {...props}>
        {children}
      </Slot>
    )
  }

  return (
    <Button
      onClick={handleClick}
      size="icon"
      type="button"
      variant="ghost"
      {...props}
    >
      {children ?? <Icon className="size-4" />}
    </Button>
  )
}

export interface MiniCalendarDaysProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'children'
> {
  children: (date: Date) => ReactNode
}

export const MiniCalendarDays = ({
  className,
  children,
  ...props
}: MiniCalendarDaysProps): ReactNode => {
  const { startDate, days: dayCount, weekdaysOnly } = useMiniCalendar()
  const days = getDays(startDate, dayCount, weekdaysOnly)

  return (
    <div className={cn('flex items-center gap-1', className)} {...props}>
      {days.map((date) => children(date))}
    </div>
  )
}

export interface MiniCalendarDayProps extends Omit<
  ComponentProps<typeof Button>,
  'color'
> {
  date: Date
  /** Label to show above the day number. Defaults to 'weekday' */
  label?: 'weekday' | 'month'
}

export const MiniCalendarDay = ({
  date,
  className,
  label = 'weekday',
  ...props
}: MiniCalendarDayProps): ReactNode => {
  const { selectedDate, onDateSelect } = useMiniCalendar()
  const { day, weekday, month } = formatDate(date)
  const isSelected = selectedDate && isSameDay(date, selectedDate)
  const isTodayDate = isToday(date)

  return (
    <Button
      className={cn(
        'h-auto min-w-12 flex-col gap-0 p-2 text-xs',
        isTodayDate && !isSelected && 'bg-accent',
        className
      )}
      onClick={() => onDateSelect(date)}
      size="sm"
      type="button"
      variant={isSelected ? 'default' : 'ghost'}
      {...props}
    >
      <span
        className={cn(
          'font-medium text-[10px] text-muted-foreground',
          isSelected && 'text-primary-foreground/70'
        )}
      >
        {label === 'weekday' ? weekday : month}
      </span>
      <span className="text-sm font-semibold">{day}</span>
    </Button>
  )
}
